import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import BookkeepingService from "../../../../../../services/BookkeepingService";
import FixedAssetRegister from "./FixedAssetRegister";
import EvidenceSlot from "../../shared/EvidenceSlot";
import WComponent from "../../../../../common/WComponent";
import { fmtBal } from "../../shared/helpers/formatters";
import "../../afs-walkthrough.css";

class LeadSheetDetail extends WComponent {
	state = {
		noteText: "",
		editingNotes: false,
		localNotes: this.props.leadSheet.notes || [],
		signingOff: false,
		documents: [],
		uploadingSlotKey: null,
		expandedAcctDocs: null,
	};

	_pollTimer = null;

	componentDidMount() {
		this.loadDocuments();
	}

	componentDidUpdate(prevProps, prevState) {
		// Reload when switching to a different lead sheet
		if (prevProps.leadSheet.wpRef !== this.props.leadSheet.wpRef) {
			this.loadDocuments();
		}
		// Re-evaluate polling whenever the documents list changes (mirrors the
		// old documents-dependency effect).
		if (prevState.documents !== this.state.documents) {
			this.evaluatePolling();
		}
	}

	componentWillUnmount() {
		if (this._pollTimer) clearInterval(this._pollTimer);
	}

	// Polls while verification is pending OR classifications are still running.
	// Classifications are "still running" when any reconciling item has no
	// account_name yet. Stops after ~90s (30 attempts x 3s).
	evaluatePolling = () => {
		if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; }
		let documents = this.state.documents;
		let anyPending = documents.some(function (d) { return d.verification && d.verification.status === "pending"; });
		let anyClassifying = documents.some(function (d) {
			if (!d.verification || !d.verification.signals || !d.verification.signals.reconciliation) return false;
			let recon = d.verification.signals.reconciliation;
			let items = (recon.on_bank_not_gl || []).concat(recon.on_gl_not_bank || []);
			return items.length > 0 && items.some(function (item) { return !item.account_name && !item.canonical_code; });
		});
		if (!anyPending && !anyClassifying) return;
		let attempts = 0;
		let maxAttempts = anyPending ? 10 : 30;
		this._pollTimer = setInterval(() => {
			attempts++;
			if (attempts > maxAttempts) { clearInterval(this._pollTimer); this._pollTimer = null; return; }
			this.loadDocuments();
		}, 3000);
	};

	loadDocuments = async () => {
		let result = await BookkeepingService.getLeadSheetDocuments({ clientId: this.props.clientId, wpRef: this.props.leadSheet.wpRef });
		if (result) this.update({ documents: result.documents || [] });
	};

	handleSlotUpload = async ({ slot, canonicalCode, expectedBalance, file }) => {
		let key = canonicalCode + "::" + slot.code;
		this.update({ uploadingSlotKey: key });
		await BookkeepingService.uploadLeadSheetDocument({
			clientId: this.props.clientId,
			wpRef: this.props.leadSheet.wpRef,
			evidenceRequirementCode: slot.code,
			canonicalCode: canonicalCode || null,
			expectedBalance: expectedBalance != null ? expectedBalance : null,
			file,
		});
		await this.loadDocuments();
		this.update({ uploadingSlotKey: null });
	};

	handleToggleReviewed = async (doc) => {
		await BookkeepingService.updateLeadSheetDocument({ documentId: doc.id, reviewed: !doc.reviewed });
		await this.loadDocuments();
	};

	handleReconAction = async ({ verificationId, side, index, action, payload }) => {
		await BookkeepingService.reconciliationAction({ verificationId, side, index, action, payload });
		await this.loadDocuments();
	};

	handleReconBulkAction = async ({ verificationId, side, indexes, action, payload }) => {
		await BookkeepingService.reconciliationBulkAction({ verificationId, side, indexes, action, payload });
		await this.loadDocuments();
	};

	handleDeleteDoc = async (doc) => {
		await BookkeepingService.deleteLeadSheetDocument({ documentId: doc.id });
		await this.loadDocuments();
	};

	findDocForSlot = ({ slot, canonicalCode }) => {
		return this.state.documents.find(function (d) {
			return d.evidenceRequirementCode === slot.code && (d.canonicalCode === canonicalCode || (!d.canonicalCode && !canonicalCode));
		}) || null;
	};

	findDocsForSlot = ({ slot, canonicalCode }) => {
		return this.state.documents.filter(function (d) {
			return d.evidenceRequirementCode === slot.code && (d.canonicalCode === canonicalCode || (!d.canonicalCode && !canonicalCode));
		});
	};

	countFilledForAccount = ({ acct }) => {
		let slots = acct.slots || [];
		let filled = 0;
		let required = 0;
		for (let s of slots) {
			if (s.waived) continue;
			if (s.required) required++;
			if (this.findDocForSlot({ slot: s, canonicalCode: acct.code })) filled++;
		}
		return { filled, total: slots.filter(function (s) { return !s.waived; }).length, required };
	};

	handleAddNote = async () => {
		let { noteText, localNotes } = this.state;
		if (!noteText.trim()) return;
		let updated = [...localNotes, { text: noteText.trim(), at: new Date().toISOString() }];
		this.update({ localNotes: updated, noteText: "" });
		if (this.props.onSaveNotes) await this.props.onSaveNotes(updated);
	};

	handleRemoveNote = async (idx) => {
		let updated = this.state.localNotes.filter(function (_, i) { return i !== idx; });
		this.update({ localNotes: updated });
		if (this.props.onSaveNotes) await this.props.onSaveNotes(updated);
	};

	handleSignoff = async (level) => {
		this.update({ signingOff: true });
		if (this.props.onSignoff) await this.props.onSignoff(level);
		this.update({ signingOff: false });
	};

	render() {
		let { leadSheet, clientId, fiscalYearEnd, onBack, onFarChange } = this.props;
		let { noteText, localNotes, signingOff, documents, uploadingSlotKey, expandedAcctDocs } = this.state;

		let ls = leadSheet;

		// Only render the Docs column if this section actually expects any supporting documents
		let sectionHasDocs = ls && ls.accounts && ls.accounts.some(function (a) { return a.slots && a.slots.length > 0; });

		return (
			<div className="afs-lead-detail">
				<div className="afs-lead-detail__header">
					<button className="afs-lead-detail__back" onClick={onBack}>
						<Icon.ArrowLeft size={16} /> Back
					</button>
					<div className="afs-lead-detail__title-block">
						<span className="afs-lead-detail__wp-ref">{ls.wpRef}</span>
						<span className="afs-lead-detail__title">{ls.title}</span>
					</div>
					<div className="afs-lead-detail__signoff-area">
						{ls.signoffs && ls.signoffs.preparer ? (
							<span className="afs-lead-detail__signed"><Icon.CheckCircle size={14} color="#1565c0" /> Prepared: {ls.signoffs.preparer.name}</span>
						) : (
							<button className="afs-lead-detail__signoff-btn" disabled={signingOff} onClick={() => this.handleSignoff("preparer")}>
								<Icon.Edit3 size={13} /> Sign as Preparer
							</button>
						)}
						{ls.signoffs && ls.signoffs.reviewer ? (
							<span className="afs-lead-detail__signed"><Icon.CheckCircle size={14} color="#2e7d32" /> Reviewed: {ls.signoffs.reviewer.name}</span>
						) : (
							<button className="afs-lead-detail__signoff-btn afs-lead-detail__signoff-btn--reviewer" disabled={signingOff} onClick={() => this.handleSignoff("reviewer")}>
								<Icon.Shield size={13} /> Sign as Reviewer
							</button>
						)}
					</div>
				</div>

				{/* Account Table — Docs column only renders when this section has expected documents */}
				<table className="afs-lead-detail__table">
					<thead>
						<tr>
							<th className="afs-lead-detail__th">Account</th>
							<th className="afs-lead-detail__th">GIFI</th>
							<th className="afs-lead-detail__th afs-lead-detail__th--right">Opening</th>
							<th className="afs-lead-detail__th afs-lead-detail__th--right">Movements</th>
							<th className="afs-lead-detail__th afs-lead-detail__th--right">Adjustments</th>
							<th className="afs-lead-detail__th afs-lead-detail__th--right">Closing</th>
							<th className="afs-lead-detail__th afs-lead-detail__th--right">Prior</th>
							{sectionHasDocs && (
								<th className="afs-lead-detail__th afs-lead-detail__th--docs">Docs</th>
							)}
						</tr>
					</thead>
					<tbody>
						{ls.accounts.map((acct) => {
							let isDocsExpanded = expandedAcctDocs === acct.code;
							let counts = this.countFilledForAccount({ acct: acct });
							let hasMissingRequired = (acct.slots || []).some((s) => {
								return s.required && !s.waived && !this.findDocForSlot({ slot: s, canonicalCode: acct.code });
							});
							return (
								<React.Fragment key={acct.code}>
									<tr className="afs-lead-detail__acct-row">
										<td className="afs-lead-detail__td">{acct.name}</td>
										<td className="afs-lead-detail__td afs-lead-detail__td--gifi">{acct.gifiCode || "—"}</td>
										<td className="afs-lead-detail__td afs-lead-detail__td--right afs-lead-detail__td--mono">{fmtBal(acct.opening)}</td>
										<td className="afs-lead-detail__td afs-lead-detail__td--right afs-lead-detail__td--mono">{acct.movements == null || acct.movements === 0 ? "—" : fmtBal(acct.movements)}</td>
										<td className={clsx("afs-lead-detail__td", "afs-lead-detail__td--right", "afs-lead-detail__td--mono", acct.adjustments !== 0 && "afs-lead-detail__td--adj")}>{acct.adjustments !== 0 ? fmtBal(acct.adjustments) : "—"}</td>
										<td className="afs-lead-detail__td afs-lead-detail__td--right afs-lead-detail__td--mono afs-lead-detail__td--bold">{fmtBal(acct.closing)}</td>
										<td className="afs-lead-detail__td afs-lead-detail__td--right afs-lead-detail__td--mono afs-lead-detail__td--muted">{fmtBal(acct.prior)}</td>
										{sectionHasDocs && (
											<td className="afs-lead-detail__td afs-lead-detail__td--center">
												<button
													className={clsx(
														"afs-lead-detail__doc-inline-btn",
														counts.filled > 0 && "afs-lead-detail__doc-inline-btn--has-docs",
														hasMissingRequired && "afs-lead-detail__doc-inline-btn--missing",
													)}
													onClick={() => this.update({ expandedAcctDocs: isDocsExpanded ? null : acct.code })}
													title={(acct.slots && acct.slots.length > 0) ? counts.filled + " of " + counts.total + " uploaded" : "No expected documents"}
												>
													<Icon.Paperclip size={13} />
													{(acct.slots && acct.slots.length > 0) && (
														<span className="afs-lead-detail__doc-inline-count">{counts.filled}/{counts.total}</span>
													)}
												</button>
											</td>
										)}
									</tr>
									{isDocsExpanded && (
										<tr>
											<td colSpan={sectionHasDocs ? 7 : 6} className="afs-lead-detail__doc-expand-cell">
												<div className="afs-lead-detail__doc-expand">
													{(!acct.slots || acct.slots.length === 0) ? (
														<div className="evidence-slot-list__empty">
															No expected supporting documents configured for this account.
														</div>
													) : (
														<div className="evidence-slot-list">
															{acct.slots.map((slot) => {
																let allDocs = this.findDocsForSlot({ slot: slot, canonicalCode: acct.code });
																let doc = allDocs.length > 0 ? allDocs[0] : null;
																let key = acct.code + "::" + slot.code;
																return (
																	<EvidenceSlot
																		key={slot.code}
																		slot={slot}
																		doc={doc}
																		allDocs={allDocs}
																		yearEnd={fiscalYearEnd || null}
																		uploading={uploadingSlotKey === key}
																		onUpload={(file) => { this.handleSlotUpload({ slot: slot, canonicalCode: acct.code, expectedBalance: acct.closing, file: file }); }}
																		onDelete={(d) => { this.handleDeleteDoc(d || doc); }}
																		onToggleReviewed={(d) => { this.handleToggleReviewed(d || doc); }}
																		onReconAction={(args) => { this.handleReconAction(args); }}
																		onReconBulkAction={(args) => { this.handleReconBulkAction(args); }}
																		onLoadDocuments={this.loadDocuments}
																		readOnly={true}
																	/>
																);
															})}
														</div>
													)}
												</div>
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})}
						<tr className="afs-lead-detail__total-row">
							<td className="afs-lead-detail__td afs-lead-detail__td--bold" colSpan={2}>TOTAL</td>

							<td className="afs-lead-detail__td afs-lead-detail__td--right afs-lead-detail__td--mono afs-lead-detail__td--bold">{fmtBal(ls.totals.opening)}</td>
							<td className="afs-lead-detail__td afs-lead-detail__td--right afs-lead-detail__td--mono afs-lead-detail__td--bold">{ls.totals.movements == null || ls.totals.movements === 0 ? "—" : fmtBal(ls.totals.movements)}</td>
							<td className={clsx("afs-lead-detail__td", "afs-lead-detail__td--right", "afs-lead-detail__td--mono", "afs-lead-detail__td--bold", ls.totals.adjustments !== 0 && "afs-lead-detail__td--adj")}>{ls.totals.adjustments !== 0 ? fmtBal(ls.totals.adjustments) : "—"}</td>
							<td className="afs-lead-detail__td afs-lead-detail__td--right afs-lead-detail__td--mono afs-lead-detail__td--bold">{fmtBal(ls.totals.closing)}</td>
							<td className="afs-lead-detail__td afs-lead-detail__td--right afs-lead-detail__td--mono afs-lead-detail__td--bold afs-lead-detail__td--muted">{fmtBal(ls.totals.prior)}</td>
							{sectionHasDocs && <td className="afs-lead-detail__td" />}
						</tr>
					</tbody>
				</table>

				{/* Fixed Asset Register — only on A-5 (PP&E) */}
				{ls.wpRef === "A-5" && (
					<div className="afs-lead-detail__section">
						<FixedAssetRegister clientId={clientId} onChange={onFarChange} />
					</div>
				)}

				{/* Adjusting Entries */}
				<div className="afs-lead-detail__section">
					<div className="afs-lead-detail__section-title">Adjusting Entries</div>
					{ls.relatedAJEs.length === 0 ? (
						<div className="afs-lead-detail__empty-section">No adjusting entries affect this section</div>
					) : (
						<div className="afs-lead-detail__aje-list">
							{ls.relatedAJEs.map(function (aje, i) {
								return (
									<div key={i} className="afs-lead-detail__aje">
										<span className={clsx("afs-lead-detail__aje-side", aje.side === "DR" ? "afs-lead-detail__aje-side--dr" : "afs-lead-detail__aje-side--cr")}>{aje.side}</span>
										<span className="afs-lead-detail__aje-account">{aje.account}</span>
										<span className="afs-lead-detail__aje-desc">{aje.description}</span>
										<span className="afs-lead-detail__aje-amount">{fmtBal(aje.amount)}</span>
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* Cross-Checks */}
				{ls.relatedChecks.length > 0 && (
					<div className="afs-lead-detail__section">
						<div className="afs-lead-detail__section-title">Related Cross-Checks</div>
						<div className="afs-lead-detail__checks">
							{ls.relatedChecks.map(function (check) {
								return (
									<div key={check.id} className={clsx("afs-lead-detail__check", check.passed ? "afs-lead-detail__check--pass" : "afs-lead-detail__check--fail")}>
										{check.passed ? <Icon.CheckCircle size={14} color="#2e7d32" /> : <Icon.XCircle size={14} color="#c62828" />}
										<span className="afs-lead-detail__check-id">{check.id}</span>
										<span className="afs-lead-detail__check-label">{check.label}</span>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* Notes / Tickmarks */}
				<div className="afs-lead-detail__section">
					<div className="afs-lead-detail__section-title">Notes & Tickmarks</div>
					{localNotes.length > 0 && (
						<div className="afs-lead-detail__notes-list">
							{localNotes.map((note, i) => {
								return (
									<div key={i} className="afs-lead-detail__note">
										<span className="afs-lead-detail__note-text">{note.text}</span>
										<button className="afs-lead-detail__note-remove" onClick={() => this.handleRemoveNote(i)}><Icon.X size={12} /></button>
									</div>
								);
							})}
						</div>
					)}
					<div className="afs-lead-detail__note-add">
						<input className="afs-lead-detail__note-input" type="text" placeholder="Add a note or tickmark..." value={noteText} onChange={(e) => this.update({ noteText: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") this.handleAddNote(); }} />
						<button className="afs-lead-detail__note-btn" onClick={this.handleAddNote} disabled={!noteText.trim()}><Icon.Plus size={14} /></button>
					</div>
				</div>

				{/* Supporting Documents Summary */}
				{documents.length > 0 && (
					<div className="afs-lead-detail__section">
						<div className="afs-lead-detail__section-title">Supporting Documents</div>
						<div className="afs-lead-detail__doc-summary">
							{documents.length} document(s) attached · {documents.filter(function (d) { return d.reviewed; }).length} reviewed
						</div>
					</div>
				)}
			</div>
		);
	}
}

export default LeadSheetDetail;
